// The app's inputs to createDashboard (@rdrive/plugin-dashboard-shell; IA_ABSORPTION_PLAN Phase 1
// steps 6b-2a/6b-2b). main.js is the global CSS plus `createDashboard(dashboardConfig).mount('#app')`;
// createDashboard creates the DASHBOARD from what is here — the platform settings, the proxy
// transport, DuckDB, the data loader over the manifest, the two filter contexts, analytics —
// then the router, i18n and Vuetify, calls the store factory with the dashboard, and installs
// router → Vuetify → store → i18n. Components reach the dashboard with useDashboard() (pages
// through composables/useAppContext.js). A JS module, not JSON: it holds components and
// functions (PLATFORM.md §3.4).
//
// IMPORT ORDER IS LOAD-BEARING FOR CSS: App first, then the pages in the order the old router.js
// imported them, then `vuetify/components` — the order main.js + router.js evaluated them in, so
// the bundle's CSS cascade is unchanged. The global CSS stays first, in main.js.
import App from './App/component.vue';
import ProjectPage from './components/ProjectPage/component.vue';
import TeamsPage from './components/TeamsPage/component.vue';
import DrawingsPage from './components/DrawingsPage/component.vue';
import ControllersPage from './components/ControllersPage/component.vue';
import DocumentsPage from './components/DocumentsPage/component.vue';
import ActivityPage from './components/ActivityPage/component.vue';
import { THEME_LIGHT } from '@rdrive/theme';
import { vocabulary } from '@rdrive/vocabulary';
import { fa } from 'vuetify/iconsets/fa';
import { mdi } from 'vuetify/iconsets/mdi';
import { md } from 'vuetify/iconsets/md';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { createAppStore } from './store';
import messages from './locales/index.js';
import { manifest } from './data/manifest';
import { itemsFilterConfig, statusHistoryFilterConfig } from './data/filters';
import { PROXY_URL, API_URL, GA_MEASUREMENT_ID } from './config/constants';

// The app's pages (was src/router.js). Route NAMES are the app bar's nav keys
// (App/setup.js `menuItems` → DashboardShell's `navItems`).
const routes = [
  { path: '/', redirect: '/project' },
  { path: '/project', name: 'Project', component: ProjectPage },
  { path: '/teams', name: 'Teams', component: TeamsPage },
  { path: '/drawings', name: 'Drawings', component: DrawingsPage },
  { path: '/documents', name: 'Documents', component: DocumentsPage },
  { path: '/controllers', name: 'Controllers', component: ControllersPage },
  { path: '/activity', name: 'Activity', component: ActivityPage },
];

// The domain store, created by createDashboard through the factory below (its actions need the
// dashboard's DuckDB connection). Bound here for the data loader's onLoaded hook, which runs
// long after — at the first data load.
let store = null;

export default {
  // The root component: the app's adapter over DashboardShell. Its onMounted calls
  // `dashboard.start()` (the platform half of the boot) and the welcome dialog's Load button
  // runs `dashboard.loader.load()`.
  App,

  routes,
  history: 'web', // createWebHistory(), no base — as router.js had it

  // The Vuex store — DOMAIN state only since 6b-2b (store/index.js). A factory: createDashboard
  // calls it with the dashboard.
  store: (dashboard) => (store = createAppStore(dashboard)),

  // The proxy host and the default API host (settings.apiUrl until the URL's `apiUrl` lands).
  transport: { proxyUrl: PROXY_URL, apiUrl: API_URL },

  // The data manifest (13 boot tables, the lazy status_history, the status_templates re-rank)
  // and the domain half of every load: the lookup objects and the items filter context, built
  // after the tables land and BEFORE `dataLoaded` flips (the R2 fix) — so no page's first query
  // reaches the `filteredItems` view before it exists.
  data: {
    manifest,
    onLoaded: async ({ filters }) => {
      await store.dispatch('loadLookupObjectsFromDB');
      await filters.items.initializeFilterContext({
        showOpenItemsOnly: store.getters.getShowOpenItemsOnly,
      });
    },
  },

  // The two filter contexts (data/filters.js holds every line of their SQL). `items` is
  // initialised by onLoaded above; `statusHistory` by ActivityPage, once it has ensured
  // `status_history`.
  filters: {
    items: itemsFilterConfig,
    statusHistory: statusHistoryFilterConfig,
  },

  // GA4: `config` once the URL settings are in (dashboard.start()), a page view per navigation,
  // and `parent_url` = settings.apiUrl on every hit.
  analytics: { measurementId: GA_MEASUREMENT_ID },

  // window.__rdriveDev (the token-refresh hook tooling/smoke/10-token-refresh.js drives) in dev only
  dev: import.meta.env.DEV,

  // @rdrive/theme's createDashboardVuetify options, exactly as main.js passed them. The wildcard
  // component/directive maps stay app-side on purpose: dropping them (redundant under autoImport)
  // is its own opt-in change with its own smoke (PACKAGING_PLAN.md §3.4); the icon sets match the
  // icon fonts main.js imports.
  vuetify: {
    defaultTheme: THEME_LIGHT,
    icons: {
      defaultSet: 'mdi',
      sets: {
        mdi,
        md,
        fa,
      },
    },
    components,
    directives,
  },

  // Root i18n: the app's page and feature text OVER rdrive's shared words (one home per key —
  // `pnpm i18n:extract` reports OVERRIDES). Its locale follows the dashboard's settings.locale,
  // normalised once by the dashboard (@rdrive/i18n's normalizeLocale).
  i18n: {
    messages,
    vocabulary,
  },
};
